// src/services/dashboardLayoutService.js
import { STORAGE_KEYS } from '../constants/storageKeys';
import apiClient from './api';
import logger from '../utils/logger';

export const GRID_COLS = { lg: 12, md: 10, sm: 6, xs: 4 };
const MIN_CARD_SIZE = 2;

class DashboardLayoutService {
  constructor() {
    // Debounce timers to prevent excessive saves
    this.saveTimers = new Map();
    // Pending API updates for batching
    this.pendingApiUpdates = new Map();
  }

  // Debounced save helper - FIXED
  debouncedSave(key, saveFunction, delay = 1000) {
    // Clear existing timer
    if (this.saveTimers.has(key)) {
      clearTimeout(this.saveTimers.get(key));
    }

    // Set new timer
    const timer = setTimeout(() => {
      saveFunction();
      this.saveTimers.delete(key);
    }, delay);

    this.saveTimers.set(key, timer);
  }

  // Save configuration to API with debouncing
  saveToApi(dashboardId, configKey, configValue) {
    // Store the pending update
    if (!this.pendingApiUpdates.has(dashboardId)) {
      this.pendingApiUpdates.set(dashboardId, {});
    }
    this.pendingApiUpdates.get(dashboardId)[configKey] = configValue;

    // Debounced API save
    this.debouncedSave(`api_${dashboardId}`, async () => {
      const updates = this.pendingApiUpdates.get(dashboardId);
      if (!updates || Object.keys(updates).length === 0) return;

      try {
        await apiClient.patch('/dashboard/preferences/', {
          dashboard_id: dashboardId,
          configuration: updates
        });
        logger.log('[DashboardLayoutService] Saved to API:', Object.keys(updates));
        this.pendingApiUpdates.delete(dashboardId);
      } catch (error) {
        console.error('[DashboardLayoutService] Failed to save to API:', error);
      }
    }, 1000);
  }

  // Load all preferences from API
  async loadFromApi(dashboardId) {
    try {
      const response = await apiClient.get(`/dashboard/preferences/?dashboard_id=${dashboardId}`);
      if (response.data && response.data.configuration) {
        return response.data.configuration;
      }
      return null;
    } catch (error) {
      console.error('[DashboardLayoutService] Failed to load from API:', error);
      return null;
    }
  }

  cardSize(card, breakpoint) {
    const cols = GRID_COLS[breakpoint];
    const responsiveWidth = breakpoint === 'lg' ? null : card.responsive?.[breakpoint]?.width;
    const w = Math.max(1, Math.min(cols, responsiveWidth || card.width || 6));
    const h = Math.max(1, card.height || 6);
    const minW = Math.min(w, MIN_CARD_SIZE);
    const minH = Math.min(h, MIN_CARD_SIZE);
    return { w, h, minW, minH };
  }

  generateDefaultLayout(cards) {
    const layouts = {};
    Object.entries(GRID_COLS).forEach(([breakpoint, cols]) => {
      let x = 0;
      let y = 0;
      let rowHeight = 0;
      layouts[breakpoint] = cards.map((card) => {
        const size = this.cardSize(card, breakpoint);
        if (x + size.w > cols) {
          x = 0;
          y += rowHeight;
          rowHeight = 0;
        }
        const item = { i: card.id, x, y, ...size };
        x += size.w;
        rowHeight = Math.max(rowHeight, size.h);
        return item;
      });
    });
    return layouts;
  }

  normalizeItem(saved, card, breakpoint, fallback) {
    const cols = GRID_COLS[breakpoint];
    const size = this.cardSize(card, breakpoint);
    if (!saved) {
      return fallback || { i: card.id, x: 0, y: Infinity, ...size };
    }
    const tooSmall = !(saved.w >= size.minW) || !(saved.h >= size.minH);
    const w = tooSmall ? size.w : Math.min(saved.w, cols);
    const h = tooSmall ? size.h : saved.h;
    const x = Number.isFinite(saved.x) && saved.x >= 0 && saved.x + w <= cols ? saved.x : 0;
    const y = Number.isFinite(saved.y) && saved.y >= 0 ? saved.y : Infinity;
    return { ...saved, i: card.id, x, y, w, h, minW: size.minW, minH: size.minH };
  }

  normalizeLayouts(layouts, cards) {
    const defaults = this.generateDefaultLayout(cards);
    const result = {};
    Object.keys(GRID_COLS).forEach((breakpoint) => {
      const savedMap = new Map((layouts?.[breakpoint] || []).map((item) => [item.i, item]));
      const defaultMap = new Map(defaults[breakpoint].map((item) => [item.i, item]));
      result[breakpoint] = cards.map((card) => this.normalizeItem(
        savedMap.get(card.id), card, breakpoint, defaultMap.get(card.id),
      ));
    });
    return result;
  }

  // Save layout - Using debounced saves to prevent excessive operations
  saveLayout(dashboardId, layout) {
    const key = `${STORAGE_KEYS.DASHBOARD_LAYOUT}_${dashboardId}`;

    const saveFunction = () => {
      try {
        localStorage.setItem(key, JSON.stringify(layout));
      } catch (error) {
        console.warn('Failed to save dashboard layout:', error);
      }
    };

    this.debouncedSave(`layout_${dashboardId}`, saveFunction, 500);
    this.saveToApi(dashboardId, 'layout', layout);
  }
  
  // Load layout
  loadLayout(dashboardId, defaultLayout = {}) {
    const key = `${STORAGE_KEYS.DASHBOARD_LAYOUT}_${dashboardId}`;
    try {
      const saved = localStorage.getItem(key);
      return saved ? JSON.parse(saved) : defaultLayout;
    } catch (error) {
      console.error('Error loading layout:', error);
      return defaultLayout;
    }
  }
  
  // Save full layouts - Using debounced saves to prevent excessive operations
  saveFullLayouts(dashboardId, fullLayouts) {
    const key = `${STORAGE_KEYS.DASHBOARD_FULL_LAYOUT}_${dashboardId}`;

    const saveFunction = () => {
      try {
        localStorage.setItem(key, JSON.stringify(fullLayouts));
      } catch (error) {
        console.warn('Failed to save dashboard full layouts:', error);
      }
    };

    this.debouncedSave(`fullLayouts_${dashboardId}`, saveFunction, 500);
    this.saveToApi(dashboardId, 'fullLayouts', fullLayouts);
  }
  
  // Load full layouts
  loadFullLayouts(dashboardId, defaultLayout = {}) {
    const key = `${STORAGE_KEYS.DASHBOARD_FULL_LAYOUT}_${dashboardId}`;
    try {
      const saved = localStorage.getItem(key);
      return saved ? JSON.parse(saved) : defaultLayout;
    } catch (error) {
      console.error('Error loading full layouts:', error);
      return defaultLayout;
    }
  }
  
  // Save visibility - Using debounced saves to prevent excessive operations
  saveVisibility(dashboardId, visibility) {
    const key = `${STORAGE_KEYS.DASHBOARD_VISIBILITY}_${dashboardId}`;

    const saveFunction = () => {
      try {
        localStorage.setItem(key, JSON.stringify(visibility));
      } catch (error) {
        console.warn('Failed to save dashboard visibility:', error);
      }
    };

    this.debouncedSave(`visibility_${dashboardId}`, saveFunction, 500);
    this.saveToApi(dashboardId, 'visibility', visibility);
  }
  
  // Load visibility
  loadVisibility(dashboardId, defaultVisibility = {}) {
    const key = `${STORAGE_KEYS.DASHBOARD_VISIBILITY}_${dashboardId}`;
    try {
      const saved = localStorage.getItem(key);
      return saved ? JSON.parse(saved) : defaultVisibility;
    } catch (error) {
      console.error('Error loading visibility:', error);
      return defaultVisibility;
    }
  }
  
  // Save sidebar visibility - Immediate save without debouncing for instant updates
  saveSidebarVisibility(dashboardId, visibility) {
    const key = `${STORAGE_KEYS.DASHBOARD_SIDEBAR_VISIBILITY}_${dashboardId}`;

    try {
      localStorage.setItem(key, JSON.stringify(visibility));
    } catch (error) {
      console.warn('Failed to save dashboard sidebar visibility:', error);
    }

    // Save to API immediately (no debouncing) to ensure persistence across logins
    this.saveToApiImmediate(dashboardId, 'sidebarVisibility', visibility);
  }

  // Save to API immediately without debouncing
  async saveToApiImmediate(dashboardId, configKey, configValue) {
    try {
      await apiClient.patch('/dashboard/preferences/', {
        dashboard_id: dashboardId,
        configuration: { [configKey]: configValue }
      });
      logger.log('[DashboardLayoutService] Saved to API immediately:', configKey);
    } catch (error) {
      console.error('[DashboardLayoutService] Failed to save to API:', error);
    }
  }
  
  // Load sidebar visibility
  loadSidebarVisibility(dashboardId, defaultVisibility = {}) {
    const key = `${STORAGE_KEYS.DASHBOARD_SIDEBAR_VISIBILITY}_${dashboardId}`;
    try {
      const saved = localStorage.getItem(key);
      return saved ? JSON.parse(saved) : defaultVisibility;
    } catch (error) {
      console.error('Error loading sidebar visibility:', error);
      return defaultVisibility;
    }
  }
  
  // Save card preferences - FIXED with proper debouncing
  saveCardPreferences(dashboardId, cardId, preferences) {
    const key = `${STORAGE_KEYS.DASHBOARD_CARD_PREFERENCES}_${dashboardId}`;

    const saveFunction = () => {
      try {
        const existing = this.loadAllCardPreferences(dashboardId);
        existing[cardId] = { ...existing[cardId], ...preferences };
        localStorage.setItem(key, JSON.stringify(existing));
        this.saveToApi(dashboardId, 'cardPreferences', existing);
      } catch (error) {
        console.error('Error saving card preferences:', error);
      }
    };

    this.debouncedSave(`cardPrefs_${dashboardId}_${cardId}`, saveFunction, 1000);
  }
  
  // Load card preferences for a specific card
  loadCardPreferences(dashboardId, cardId, defaultPreferences = {}) {
    const allPreferences = this.loadAllCardPreferences(dashboardId);
    return { ...defaultPreferences, ...allPreferences[cardId] };
  }
  
  // Load all card preferences
  loadAllCardPreferences(dashboardId) {
    const key = `${STORAGE_KEYS.DASHBOARD_CARD_PREFERENCES}_${dashboardId}`;
    try {
      const saved = localStorage.getItem(key);
      return saved ? JSON.parse(saved) : {};
    } catch (error) {
      console.error('Error loading card preferences:', error);
      return {};
    }
  }

  // Clear dashboard settings
  clearDashboardSettings(dashboardId) {
    const layoutKey = `${STORAGE_KEYS.DASHBOARD_LAYOUT}_${dashboardId}`;
    const fullLayoutKey = `${STORAGE_KEYS.DASHBOARD_FULL_LAYOUT}_${dashboardId}`;
    const visibilityKey = `${STORAGE_KEYS.DASHBOARD_VISIBILITY}_${dashboardId}`;
    const sidebarVisibilityKey = `${STORAGE_KEYS.DASHBOARD_SIDEBAR_VISIBILITY}_${dashboardId}`;
    const cardPreferencesKey = `${STORAGE_KEYS.DASHBOARD_CARD_PREFERENCES}_${dashboardId}`;

    localStorage.removeItem(layoutKey);
    localStorage.removeItem(fullLayoutKey);
    localStorage.removeItem(visibilityKey);
    localStorage.removeItem(sidebarVisibilityKey);
    localStorage.removeItem(cardPreferencesKey);
  }

  // Sync all preferences from API to localStorage
  async syncFromApi(dashboardId) {
    try {
      const apiConfig = await this.loadFromApi(dashboardId);
      if (!apiConfig) {
        logger.log('[DashboardLayoutService] No API config found for:', dashboardId);
        return null;
      }

      logger.log('[DashboardLayoutService] Syncing from API:', Object.keys(apiConfig));

      // Sync each config to localStorage
      if (apiConfig.layout) {
        localStorage.setItem(
          `${STORAGE_KEYS.DASHBOARD_LAYOUT}_${dashboardId}`,
          JSON.stringify(apiConfig.layout)
        );
      }
      if (apiConfig.fullLayouts) {
        localStorage.setItem(
          `${STORAGE_KEYS.DASHBOARD_FULL_LAYOUT}_${dashboardId}`,
          JSON.stringify(apiConfig.fullLayouts)
        );
      }
      if (apiConfig.visibility) {
        localStorage.setItem(
          `${STORAGE_KEYS.DASHBOARD_VISIBILITY}_${dashboardId}`,
          JSON.stringify(apiConfig.visibility)
        );
      }
      if (apiConfig.sidebarVisibility) {
        localStorage.setItem(
          `${STORAGE_KEYS.DASHBOARD_SIDEBAR_VISIBILITY}_${dashboardId}`,
          JSON.stringify(apiConfig.sidebarVisibility)
        );
      }
      if (apiConfig.cardPreferences) {
        localStorage.setItem(
          `${STORAGE_KEYS.DASHBOARD_CARD_PREFERENCES}_${dashboardId}`,
          JSON.stringify(apiConfig.cardPreferences)
        );
      }

      return apiConfig;
    } catch (error) {
      console.error('[DashboardLayoutService] Failed to sync from API:', error);
      return null;
    }
  }
}

export const dashboardLayoutService = new DashboardLayoutService();